<div class="contract-list-container">
  <mat-card class="contracts-card">
    <mat-card-header class="card-header">
      <div class="header-content">
        <mat-card-title class="page-title"> Contratos </mat-card-title>
        @if (showSubtitle()) {
          <mat-card-subtitle>
            Gerencie os contratos dos clientes
          </mat-card-subtitle>
        }
      </div>
      <div class="header-actions">
        <button
          mat-icon-button
          (click)="refreshData()"
          matTooltip="Atualizar dados"
          [disabled]="isLoading"
        >
          <mat-icon>refresh</mat-icon>
        </button>
        <button
          mat-stroked-button
          color="primary"
          (click)="onCreate()"
          [disabled]="isLoading"
        >
          <mat-icon>add</mat-icon>
          Cadastrar Contrato
        </button>
      </div>
    </mat-card-header>

    <mat-card-content class="card-content">
      <!-- Search Section -->
      <div class="search-section">
        <mat-form-field appearance="outline" class="search-field">
          <mat-label>Buscar contratos</mat-label>
          <input
            matInput
            (keyup)="onSearch($event)"
            placeholder="Buscar por número do contrato ou cliente..."
            maxlength="100"
          />
          <mat-icon matSuffix>search</mat-icon>
          <mat-hint
            >{{ dataSource.data.length }} de
            {{ totalContracts }} contratos</mat-hint
          >
        </mat-form-field>
      </div>

      <!-- Loading Spinner -->
      @if (isLoading) {
        <div class="loading-container">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Carregando contratos...</p>
        </div>
      }

      <!-- Contracts Table -->
      @if (!isLoading) {
        <div class="table-container">
          <table
            #table
            mat-table
            [dataSource]="dataSource"
            matSort
            class="contracts-table"
          >
            <!-- Contract Number Column -->
            <ng-container matColumnDef="contractNumber">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header="contractNumber"
              >
                Número do Contrato
              </th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="contract-number-cell"
              >
                <div class="contract-info">
                  <span class="contract-number">{{
                    contract.contractNumber
                  }}</span>
                  <br />
                  <small class="contract-id">ID: {{ contract.id }}</small>
                </div>
              </td>
            </ng-container>

            <!-- Organization Column -->
            <ng-container matColumnDef="organization">
              <th
                mat-header-cell
                *matHeaderCellDef
                mat-sort-header="organizationId"
              >
                Cliente
              </th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="organization-cell"
              >
                <div class="organization-info">
                  <span class="organization-name">{{
                    contract.organization.name
                  }}</span>
                  <small class="organization-cnpj">{{
                    contract.organization.cnpj | cnpj
                  }}</small>
                </div>
              </td>
            </ng-container>

            <!-- Period Column -->
            <ng-container matColumnDef="period">
              <th mat-header-cell *matHeaderCellDef mat-sort-header="startDate">
                Período
              </th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="period-cell"
              >
                <div class="period-info">
                  <span class="period-dates">
                    {{ formatDateRange(contract.startDate, contract.endDate) }}
                  </span>
                  <br />
                  <small class="period-duration">
                    {{
                      getPeriodDurationInDays(
                        contract.startDate,
                        contract.endDate
                      )
                    }}
                    dias
                  </small>
                </div>
              </td>
            </ng-container>

            <!-- Modality Column -->
            <ng-container matColumnDef="modality">
              <th mat-header-cell *matHeaderCellDef>Modalidade</th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="modality-cell"
              >
                <mat-chip [class]="getModalityChipClass(contract.modality)">
                  {{ getModalityLabel(contract.modality) }}
                </mat-chip>
              </td>
            </ng-container>

            <!-- Quantities Column -->
            <ng-container matColumnDef="quantities">
              <th mat-header-cell *matHeaderCellDef>Quantitativos</th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="quantities-cell"
              >
                <div class="quantities-info">
                  <span class="quantities-text">{{
                    getQuantitiesSummary(contract)
                  }}</span>
                </div>
              </td>
            </ng-container>

            <!-- Status Column -->
            <ng-container matColumnDef="status">
              <th mat-header-cell *matHeaderCellDef>Status</th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="status-cell"
              >
                <mat-chip [class]="getStatusChipClass(contract)">
                  {{ getContractStatus(contract) }}
                </mat-chip>
              </td>
            </ng-container>

            <!-- Actions Column -->
            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef class="actions-header">
                Ações
              </th>
              <td
                mat-cell
                *matCellDef="let contract; table: table"
                class="actions-cell"
              >
                <button
                  mat-icon-button
                  [matMenuTriggerFor]="actionsMenu"
                  matTooltip="Ações"
                >
                  <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #actionsMenu="matMenu">
                  <button mat-menu-item (click)="onView(contract)">
                    <mat-icon>visibility</mat-icon>
                    <span>Visualizar</span>
                  </button>
                  <button mat-menu-item (click)="onEdit(contract)">
                    <mat-icon>edit</mat-icon>
                    <span>Editar</span>
                  </button>
                  <mat-divider></mat-divider>
                  <button
                    mat-menu-item
                    (click)="onDelete(contract)"
                    class="delete-action"
                  >
                    <mat-icon>delete</mat-icon>
                    <span>Excluir</span>
                  </button>
                </mat-menu>
              </td>
            </ng-container>

            <!-- Header Row -->
            <tr
              mat-header-row
              *matHeaderRowDef="displayedColumns"
              class="header-row"
            ></tr>

            <!-- Data Rows -->
            <tr
              mat-row
              *matRowDef="let row; columns: displayedColumns"
              class="data-row"
            ></tr>

            <!-- No Data Row -->
            <tr class="mat-row no-data-row" *matNoDataRow>
              <td
                class="mat-cell no-data-cell"
                [attr.colspan]="displayedColumns.length"
              >
                <div class="no-data-content">
                  <mat-icon>description</mat-icon>
                  <h3>Nenhum contrato encontrado</h3>
                  @if (searchText) {
                    <p>
                      Não foram encontrados contratos para "{{ searchText }}"
                    </p>
                  } @else {
                    <p>Não há contratos cadastrados no sistema</p>
                  }

                  <button
                    mat-raised-button
                    color="primary"
                    (click)="onCreate()"
                  >
                    <mat-icon>add</mat-icon>
                    Cadastrar Primeiro Contrato
                  </button>
                </div>
              </td>
            </tr>
          </table>
        </div>
      }

      <!-- Pagination -->
      @if (!isLoading && totalContracts > 0) {
        <mat-paginator
          [length]="totalContracts"
          [pageSize]="pageSize"
          [pageIndex]="pageIndex"
          [showFirstLastButtons]="true"
          (page)="onPageChange($event)"
          class="contracts-paginator"
        >
        </mat-paginator>
      }
    </mat-card-content>
  </mat-card>
</div>
